import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { TextField, FormField } from "../shared/FormField";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { CustomerPicker } from "./CustomerPicker";
import { CustomerFormDialog } from "../customers/CustomerFormDialog";
import { apiClient, getApiErrorMessage, getFieldErrors } from "../../lib/apiClient";
import { putWithVersion, isVersionConflict, conflictMessage, conflictLatest } from "../../lib/conflict";
import { formatCurrency, formatDate } from "../../lib/utils";

const DAY_OPTIONS = [
  { key: "sunday", label: "Sun" }, { key: "monday", label: "Mon" }, { key: "tuesday", label: "Tue" },
  { key: "wednesday", label: "Wed" }, { key: "thursday", label: "Thu" }, { key: "friday", label: "Fri" },
  { key: "saturday", label: "Sat" },
];

const EMPTY_FORM = {
  customer_id: null, customer_label: "", plan_id: "", type: "2", start_date: "",
  status: "1", payment_method: "online", day_names: [], addon_ids: [], delivery_notes: "", internal_notes: "", packaging_notes: "",
};

// Shared subscription form — rendered inside SubscriptionFormDialog (create) and
// inline on the subscription profile (edit). `active` re-initialises when it
// becomes true (the dialog passes its `open` flag; inline passes true on mount).
export function SubscriptionForm({ subscription, active = true, onSaved, onCancel, className }) {
  const isEdit = Boolean(subscription);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [plans, setPlans] = useState([]);
  const [addons, setAddons] = useState([]);
  const [outletDays, setOutletDays] = useState({ sunday: 1, monday: 1, tuesday: 1, wednesday: 1, thursday: 1, friday: 1, saturday: 1 });
  const [preview, setPreview] = useState({ loading: false, price: null, end_date: null, deliveries_total: null });
  const [customerDialogOpen, setCustomerDialogOpen] = useState(false);
  const [conflict, setConflict] = useState(null); // { message, latest }
  const versionRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!active) return;
    setErrors({});
    setConflict(null);
    versionRef.current = subscription?.updated_at || null;
    setPreview({ loading: false, price: null, end_date: null, deliveries_total: null });
    let cancelled = false;

    (async () => {
      let planOptions = [];
      try {
        const { data } = await apiClient.get("/plans/active");
        planOptions = data;
        if (subscription && !data.some((p) => p.id === subscription.plan_id)) {
          try {
            const { data: current } = await apiClient.get(`/plans/${subscription.plan_id}`);
            planOptions = [...data, current];
          } catch { /* plan no longer exists */ }
        }
      } catch { /* keep empty */ }

      let addonOptions = [];
      try {
        const { data } = await apiClient.get("/addons", { params: { limit: 200 } });
        addonOptions = data.items;
      } catch { /* keep empty */ }

      let openDays = outletDays;
      try {
        const { data } = await apiClient.get("/outlet/deliver-days");
        openDays = data;
      } catch { /* keep default (all open) */ }

      if (cancelled) return;
      setPlans(planOptions);
      setAddons(addonOptions);
      setOutletDays(openDays);

      if (subscription) {
        setForm({
          customer_id: subscription.customer_id, customer_label: subscription.customer_name || "",
          plan_id: "", type: String(subscription.type || "2"),
          start_date: subscription.start_date || "", status: String(subscription.status || "1"),
          day_names: Object.entries(subscription.deliver_days || {}).filter(([k, v]) => v && openDays[k]).map(([k]) => k),
          addon_ids: (subscription.addons || []).map((a) => a.id),
          delivery_notes: subscription.delivery_notes || "", internal_notes: subscription.internal_notes || "",
          packaging_notes: subscription.packaging_notes || "",
        });
      } else {
        setForm(EMPTY_FORM);
      }
    })();

    return () => { cancelled = true; };
    // `outletDays` is only read here as a fallback default and is set by this
    // same effect; adding it to the deps would cause an infinite reload loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, subscription]);

  // Radix Select resets the value if it's set in the same commit as its matching
  // <option> first mounts, so we defer the edit plan_id by a tick.
  useEffect(() => {
    if (!active || !subscription || plans.length === 0) return;
    setForm((f) => (f.plan_id ? f : { ...f, plan_id: String(subscription.plan_id) }));
  }, [active, subscription, plans]);

  useEffect(() => {
    if (!active || !form.plan_id || !form.start_date) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setPreview((p) => ({ ...p, loading: true }));
      try {
        const { data } = await apiClient.get("/subscriptions/preview", {
          params: {
            plan_id: form.plan_id, start_date: form.start_date,
            addon_ids: form.addon_ids.join(",") || undefined,
            deliver_days: form.day_names.join(",") || undefined,
          },
        });
        setPreview({ loading: false, price: data.price, end_date: data.end_date, deliveries_total: data.deliveries_total });
      } catch {
        setPreview({ loading: false, price: null, end_date: null, deliveries_total: null });
      }
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [active, form.plan_id, form.start_date, form.addon_ids, form.day_names]);

  const toggleDay = (day) => {
    if (!outletDays[day]) return;
    setForm((f) => ({
      ...f,
      day_names: f.day_names.includes(day) ? f.day_names.filter((d) => d !== day) : [...f.day_names, day],
    }));
  };

  const toggleAddon = (id) => {
    setForm((f) => ({
      ...f,
      addon_ids: f.addon_ids.includes(id) ? f.addon_ids.filter((a) => a !== id) : [...f.addon_ids, id],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const nextErrors = {};
    if (!form.customer_id) nextErrors.customer_id = "Select a customer to continue.";
    if (!form.plan_id) nextErrors.plan_id = "Select a plan to continue.";
    if (!form.start_date) nextErrors.start_date = "Pick a start date to continue.";
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }
    setSaving(true);
    setErrors({});
    const payload = {
      customer_id: form.customer_id,
      plan_id: Number(form.plan_id),
      start_date: form.start_date,
      end_date: preview.end_date || null,
      type: Number(form.type),
      status: isEdit ? subscription.status : Number(form.status),
      delivery_notes: form.delivery_notes || null,
      internal_notes: form.internal_notes || null,
      packaging_notes: form.packaging_notes || null,
      deactivation_reason: isEdit ? subscription.deactivation_reason : null,
      addon_ids: form.addon_ids,
      deliver_days: form.day_names,
      ...(isEdit ? {} : { payment_method: form.payment_method }),
    };
    try {
      if (isEdit) {
        await putWithVersion(`/subscriptions/${subscription.id}`, payload, versionRef.current);
        toast.success("Subscription updated");
      } else {
        const { data } = await apiClient.post("/subscriptions", payload);
        if (data.payment_link) {
          toast.success("Subscription created — payment link ready", {
            description: data.payment_link.url,
            action: { label: "Copy Link", onClick: () => navigator.clipboard.writeText(data.payment_link.url) },
          });
        } else {
          toast.success("Subscription created — pending until cash payment is approved");
        }
      }
      onSaved && onSaved();
    } catch (error) {
      if (isVersionConflict(error)) {
        const latest = conflictLatest(error);
        if (latest?.updated_at) versionRef.current = latest.updated_at;
        setConflict({ message: conflictMessage(error), latest });
        toast.warning(conflictMessage(error));
      } else {
        setErrors(getFieldErrors(error));
        toast.error(getApiErrorMessage(error, "Could not save subscription."));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className={className || "space-y-4"} data-testid="subscription-form">
      {conflict && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm" data-testid="subscription-conflict-banner">
          <p className="font-medium text-amber-900">{conflict.message}</p>
          <p className="mt-1 text-xs text-amber-800">
            Your edits are kept — review the latest server values, then press Save again to apply your changes over the latest.
          </p>
        </div>
      )}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField label="Customer" required error={errors.customer_id} testId="subscription-form-customer">
          <CustomerPicker
            value={form.customer_id}
            label={form.customer_label}
            disabled={isEdit}
            onSelect={(id, name) => setForm((f) => ({ ...f, customer_id: id, customer_label: name }))}
            onAddNew={isEdit ? undefined : () => setCustomerDialogOpen(true)}
            testId="subscription-form-customer-picker"
          />
        </FormField>

        <FormField label="Plan" required error={errors.plan_id} testId="subscription-form-plan">
          <Select value={form.plan_id} onValueChange={(v) => setForm((f) => ({ ...f, plan_id: v }))}>
            <SelectTrigger data-testid="subscription-form-plan-select"><SelectValue placeholder="Select a plan..." /></SelectTrigger>
            <SelectContent>
              {plans.map((p) => (
                <SelectItem key={p.id} value={String(p.id)}>
                  {p.name} — {formatCurrency(p.price)} {p.status !== 1 ? "(Inactive)" : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>

        <TextField
          label="Start Date" required type="date" value={form.start_date}
          onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))}
          error={errors.start_date} testId="subscription-form-start-date"
        />

        <FormField label="Type" required error={errors.type} testId="subscription-form-type">
          <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}>
            <SelectTrigger data-testid="subscription-form-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="1">Pickup</SelectItem>
              <SelectItem value="2">Delivery</SelectItem>
            </SelectContent>
          </Select>
        </FormField>

        {!isEdit && (
          <FormField label="Payment Method" required testId="subscription-form-payment-method">
            <Select value={form.payment_method} onValueChange={(v) => setForm((f) => ({ ...f, payment_method: v }))}>
              <SelectTrigger data-testid="subscription-form-payment-method-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="online">Pay Online — send payment link</SelectItem>
                <SelectItem value="cash">Pay at Store — cash, pending approval</SelectItem>
              </SelectContent>
            </Select>
          </FormField>
        )}
      </div>

      <FormField label="Delivery Days" testId="subscription-form-deliver-days">
        <div className="flex flex-wrap gap-3" data-testid="subscription-form-deliver-days-grid">
          {DAY_OPTIONS.map((d) => {
            const closed = !outletDays[d.key];
            return (
              <label
                key={d.key}
                className={`flex items-center gap-1.5 text-sm ${closed ? "text-text-muted/50" : "text-text-main"}`}
                data-testid={`subscription-form-day-${d.key}`}
                title={closed ? "Outlet is closed this day (Settings > Delivery Days)" : undefined}
              >
                <Checkbox checked={form.day_names.includes(d.key)} disabled={closed} onCheckedChange={() => toggleDay(d.key)} />
                {d.label}
              </label>
            );
          })}
        </div>
        <p className="mt-1 text-[11px] text-text-muted">Greyed-out days are closed outlet-wide (Settings &gt; Delivery Days).</p>
      </FormField>

      {addons.length > 0 && (
        <FormField label="Addons" testId="subscription-form-addons">
          <div className="grid grid-cols-1 gap-2 rounded-md border border-border bg-background p-3 sm:grid-cols-2" data-testid="subscription-form-addons-grid">
            {addons.map((a) => (
              <label key={a.id} className="flex items-center gap-2 text-sm text-text-main" data-testid={`subscription-form-addon-${a.id}`}>
                <Checkbox checked={form.addon_ids.includes(a.id)} onCheckedChange={() => toggleAddon(a.id)} />
                <span className="truncate">{a.name} {a.status !== 1 && <span className="text-text-muted">(Inactive)</span>}</span>
                <span className="ml-auto flex-shrink-0 text-xs text-text-muted">{formatCurrency(a.price)}</span>
              </label>
            ))}
          </div>
        </FormField>
      )}

      <div className="rounded-md border border-accent/40 bg-accent/10 p-4" data-testid="subscription-form-preview">
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-primary">Server-Calculated Preview</p>
        <div className="grid grid-cols-3 gap-4 text-sm">
          <div>
            <p className="text-xs text-text-muted">Price</p>
            <p className="font-heading font-semibold text-text-main" data-testid="subscription-form-preview-price">
              {preview.loading ? "..." : preview.price != null ? formatCurrency(preview.price) : "—"}
            </p>
          </div>
          <div>
            <p className="text-xs text-text-muted">Projected End Date</p>
            <p className="font-heading font-semibold text-text-main" data-testid="subscription-form-preview-end-date">
              {preview.loading ? "..." : preview.end_date ? formatDate(preview.end_date) : "Select delivery days"}
            </p>
          </div>
          <div>
            <p className="text-xs text-text-muted">Total Deliveries</p>
            <p className="font-heading font-semibold text-text-main" data-testid="subscription-form-preview-deliveries">
              {preview.loading ? "..." : preview.deliveries_total ?? "—"}
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <FormField label="Delivery Notes" testId="subscription-form-delivery-notes">
          <Textarea value={form.delivery_notes} onChange={(e) => setForm((f) => ({ ...f, delivery_notes: e.target.value }))} data-testid="subscription-form-delivery-notes-input" />
        </FormField>
        <FormField label="Internal Notes" testId="subscription-form-internal-notes">
          <Textarea value={form.internal_notes} onChange={(e) => setForm((f) => ({ ...f, internal_notes: e.target.value }))} data-testid="subscription-form-internal-notes-input" />
        </FormField>
        <FormField label="Packaging Notes" testId="subscription-form-packaging-notes">
          <Textarea value={form.packaging_notes} onChange={(e) => setForm((f) => ({ ...f, packaging_notes: e.target.value }))} data-testid="subscription-form-packaging-notes-input" />
        </FormField>
      </div>

      <div className="flex justify-end gap-2">
        <Button type="button" variant="secondary" onClick={onCancel} data-testid="subscription-form-cancel-button">
          Cancel
        </Button>
        <Button type="submit" variant="accent" disabled={saving} data-testid="subscription-form-submit-button">
          {saving ? "Saving..." : isEdit ? "Save Changes" : "Create Subscription"}
        </Button>
      </div>

      <CustomerFormDialog
        open={customerDialogOpen}
        onOpenChange={setCustomerDialogOpen}
        onSaved={(created) => {
          setCustomerDialogOpen(false);
          if (created && created.id) {
            const name = `${created.firstname || ""} ${created.lastname || ""}`.trim();
            setForm((f) => ({ ...f, customer_id: created.id, customer_label: name || `Customer #${created.id}` }));
            setErrors((e) => ({ ...e, customer_id: undefined }));
          }
        }}
      />
    </form>
  );
}
